import React, { useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";

import Navbar from "../../components/Navbar/Navbar";
import Hero from "../../components/Hero/Hero";
import Footer from "../../components/Footer/Footer";

import fieldImgOne from "../../assets/images/services.png";
import fieldImgOther from "../../assets/images/otherservices.png";
import fieldImgTwo from "../../assets/images/field-img-2.jpg";
import fieldImgSix from "../../assets/images/field-img-6.jpg";
import fieldImgSeven from "../../assets/images/field-img-7.jpg";
import fieldImgEight from "../../assets/images/field-img-8.JPG";
import fieldImgNine from "../../assets/images/field-img-9.jpg";
import fieldImgTen from "../../assets/images/field-img-10.jpg";
import fieldImgEleven from "../../assets/images/field-img-11.jpg";
import fieldImgTwelve from "../../assets/images/field-img-12.jpg";
import fieldImgThirteen from "../../assets/images/field-img-13.jpg";
import fieldImgFourteen from "../../assets/images/field-img-14.jpg";
import fieldImgTwentyFour from "../../assets/images/field-img-24.jpg";
import fieldImgTwentyFive from "../../assets/images/field-img-25.jpg";
import fieldImgTwentySix from "../../assets/images/field-img-26.jpg";
import fieldImgTwentySeven from "../../assets/images/field-img-27.jpg";
import fieldImgTwentyEight from "../../assets/images/field-img-28.jpg";
import fieldImgThirtySix from "../../assets/images/field-img-36.jpg";
import fieldImgTwentyOne from "../../assets/images/field-img-21.jpg";
import fieldImgTwentyTwo from "../../assets/images/field-img-22.jpg";
import fieldImgNineteen from "../../assets/images/field-img-19.JPG";
import fieldImgForty from "../../assets/images/field-img-40.jpg";

import WaterDropIcon from "@mui/icons-material/WaterDrop";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import BusinessIcon from "@mui/icons-material/Business";
import EngineeringIcon from "@mui/icons-material/Engineering";
import CommitIcon from "@mui/icons-material/Commit";
import ViewModuleIcon from "@mui/icons-material/ViewModule";

import classes from "./Services.module.scss";

const servicesOffered = [
  {
    title: "Waterproofing",
    description:
      "In strata buildings, water ingress is a major concern. Leaks from inadequate or missing waterproofing membranes can lead to significant damage, requiring swift diagnosis and thorough repairs. We also assist with window and door replacement where worn fixtures compromise security, energy efficiency and building performance.",
    icon: <WaterDropIcon className={classes["icon"]} />,
    images: [fieldImgThirtySix, fieldImgTwentyFour, fieldImgForty],
  },
  {
    title: "Balustrade Replacements and Upgrades",
    description:
      "Balustrade replacements and upgrades are vital for ensuring safety, compliance and presentation in strata buildings. Over time, balustrades can deteriorate and create risks that require professional intervention to restore structural integrity and improve appearance.",
    icon: <AccountBalanceIcon className={classes["icon"]} />,
    images: [fieldImgOne, fieldImgTwo],
  },
  {
    title: "Spalling",
    description:
      "Spalling is a common issue in strata buildings where concrete begins to crack, flake or break away, often caused by water ingress and corrosion. Timely repair helps protect structural integrity and prevent further deterioration.",
    icon: <BusinessIcon className={classes["icon"]} />,
    images: [
      fieldImgNineteen,
      fieldImgSeven,
      fieldImgTwentyOne,
      fieldImgTwentyTwo,
    ],
  },
  {
    title: "Structural",
    description:
      "Structural repairs are critical for maintaining the safety and longevity of strata buildings. We address issues affecting concrete, foundations, load-bearing elements and other structural components with a practical, compliance-focused approach.",
    icon: <EngineeringIcon className={classes["icon"]} />,
    images: [fieldImgSix, fieldImgEight, fieldImgNine, fieldImgTen],
  },
  {
    title: "Lintel Replacement & Brick Stitching",
    description:
      "Lintel replacement is required when supporting metal above doors and windows becomes corroded. Replacing lintels and stitching affected brickwork helps minimise damage to building elements and prolong the life of the structure.",
    icon: <CommitIcon className={classes["icon"]} />,
    images: [
      fieldImgEleven,
      fieldImgTwelve,
      fieldImgThirteen,
      fieldImgFourteen,
    ],
  },
  {
    title: "Cavity Flashing Work",
    description:
      "Cavity flashing work helps manage water movement within walls and protects buildings from moisture-related damage. Correct installation and repair is essential for long-term building performance.",
    icon: <ViewModuleIcon className={classes["icon"]} />,
    images: [
      fieldImgTwentyFive,
      fieldImgTwentySix,
      fieldImgTwentySeven,
      fieldImgTwentyEight,
    ],
  },
];

const Services = () => {
  const displayRef = useRef(null);
  const navigate = useNavigate();

  const [state, setState] = useState({
    selected: 0,
  });

  const selectService = (index) => {
    setState((prevState) => ({
      ...prevState,
      selected: index,
    }));

    displayRef.current.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "start",
    });
  };

  const selectedService = servicesOffered[state.selected];

  return (
    <div className={classes["services"]}>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Services | Unified Building Group</title>
        <meta
          name="description"
          content="Unified Building Group provides remedial building services across NSW including waterproofing, balustrade upgrades, spalling repairs, structural repairs, lintel replacement and cavity flashing work."
        />
      </Helmet>

      <Navbar />
      <Hero
        title="Our Services"
        slogan="Remedial Building, Facade Upgrades & Structural Repairs"
        image={fieldImgOne}
      />

      <main>
        <section className={classes["services-offered"]}>
          <div className={classes["section-header"]}>
            <p>What we do</p>
            <h1>Services Offered</h1>
          </div>

          <div className={classes["services-grid"]}>
            {servicesOffered.map((service, index) => (
              <button
                type="button"
                className={`${classes["service"]} ${
                  state.selected === index ? classes["selected"] : ""
                }`}
                key={service.title}
                onClick={() => selectService(index)}
              >
                <span className={classes["service-icon"]}>{service.icon}</span>
                <h2>{service.title}</h2>
              </button>
            ))}
          </div>

          <div className={classes["services-display"]} ref={displayRef}>
            <div className={classes["display-copy"]}>
              <p className={classes["eyebrow"]}>Selected service</p>
              <h2>{selectedService.title}</h2>
              <p>{selectedService.description}</p>
            </div>

            <div className={classes["image-container"]}>
              {selectedService.images.map((image, index) => (
                <img
                  key={`${selectedService.title}-${index}`}
                  src={image}
                  alt={`${selectedService.title} project example ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </section>

        <section className={classes["section-full-left"]}>
          <div className={classes["section-full-left-text-col"]}>
            <p className={classes["eyebrow"]}>Additional capabilities</p>
            <h1>Other services include:</h1>

            <ul>
              <li>Cavity Flashings</li>
              <li>Water Leaking Detection</li>
              <li>Joint Sealant Rendering</li>
              <li>Leaking Bathrooms</li>
              <li>Leaking Planter Boxes</li>
              <li>Façade Rectifications & Modifications</li>
              <li>Roof Repairs & Restoration</li>
              <li>Fire Protection Services</li>
              <li>External & Internal Painting</li>
            </ul>

            <button
              className={classes["btn"]}
              onClick={() => {
                navigate("/contact");
              }}
            >
              <span>Get in touch</span>
            </button>
          </div>

          <div className={classes["section-full-left-img-col"]}>
            <img
              src={fieldImgOther}
              alt="Unified Building Group service work"
            />
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Services;
